import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { getTeamLogoUrl } from '../../utils/formatters';
import SteamFlag from '../common/SteamFlag';
import { fetchCountriesBulk } from '../../utils/countries';
import './Leagues.css';

/**
 * Render a competitor (player or team) as a clickable chip with flag/logo.
 *
 * @param {object} props - Component props.
 * @param {object|null} props.competitor - The competitor summary from the API.
 * @param {Function} props.onOpen - Click handler navigating to the profile/team.
 * @returns {JSX.Element} The competitor chip (a muted dash when absent).
 */
function Competitor({ competitor, onOpen }) {
  if (!competitor) return <span className="hof-competitor is-empty">–</span>;
  const isTeam = competitor.type === 'team';
  const logo = isTeam ? getTeamLogoUrl(competitor.logo_url) : null;
  return (
    <button className="hof-competitor" onClick={() => onOpen(competitor)}>
      {isTeam ? (
        logo ? (
          <img className="team-logo team-logo-sm" src={logo} alt="" />
        ) : (
          <span className="team-logo team-logo-sm team-logo-blank">
            {(competitor.tag || competitor.name || '?')[0]}
          </span>
        )
      ) : (
        <SteamFlag steamid64={competitor.steamid64} className="hof-flag" />
      )}
      <span className="hof-competitor-name">{competitor.name}</span>
    </button>
  );
}

/**
 * League hall of fame: a champions roll (winner + runner-up of each finished
 * season) and an all-time honours table (titles/runner-ups/podiums/seasons),
 * keyed by the persistent competitor. Renders nothing until the league has at
 * least one finished season, so it can be dropped into the overview tab.
 *
 * @param {object} props - Component props.
 * @param {string} props.leagueId - The league id to fetch the hall of fame for.
 * @returns {JSX.Element|null} The hall-of-fame sections, or null when empty.
 */
export default function LeagueHallOfFame({ leagueId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/leagues/${leagueId}/hall-of-fame`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        setData(d);
        if (d) {
          const ids = [];
          (d.honours || []).forEach((h) => h.steamid64 && ids.push(h.steamid64));
          (d.champions || []).forEach((c) => {
            if (c.champion?.steamid64) ids.push(c.champion.steamid64);
            if (c.runner_up?.steamid64) ids.push(c.runner_up.steamid64);
          });
          if (ids.length) fetchCountriesBulk(ids);
        }
      })
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, [leagueId]);

  if (!data || data.finished_count === 0) return null;

  const openCompetitor = (c) => {
    if (!c) return;
    if (c.type === 'team') navigate(`/leagues/teams/${c.id}`);
    else if (c.steamid64) navigate(`/stats/players/${c.steamid64}`);
  };

  return (
    <div className="hof-wrap">
      <h2 className="league-section-title">🏛️ Hall of Fame</h2>

      <div className="team-card notched-card hof-card">
        <h3 className="division-title">{t('leagues.championsPerSeason')}</h3>
        <ul className="hof-champions">
          {data.champions.map((c) => (
            <li key={c.season.id} className="hof-champion-row">
              <span className="hof-season">{c.season.name}</span>
              <span className="hof-champion-main">
                <span className="hof-medal">🥇</span>
                <Competitor competitor={c.champion} onOpen={openCompetitor} />
              </span>
              {c.runner_up && (
                <span className="hof-runner-up">
                  <span className="hof-medal">🥈</span>
                  <Competitor competitor={c.runner_up} onOpen={openCompetitor} />
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="team-card notched-card hof-card">
        <h3 className="division-title">{t('leagues.allTimeHonours')}</h3>
        <table className="hof-table">
          <thead>
            <tr>
              <th className="hof-th-rank">#</th>
              <th>{t('leagues.competitor')}</th>
              <th title={t('leagues.titlesTitle')}>🥇</th>
              <th title={t('leagues.runnerUpsTitle')}>🥈</th>
              <th title={t('leagues.podiumsTitle')}>{t('leagues.podium')}</th>
              <th title={t('leagues.seasonsPlayedTitle')}>{t('leagues.seasonsShort')}</th>
            </tr>
          </thead>
          <tbody>
            {data.honours.map((h, i) => (
              <tr key={`${h.type}-${h.id}`}>
                <td className="hof-td-rank">{i + 1}</td>
                <td>
                  <Competitor competitor={h} onOpen={openCompetitor} />
                </td>
                <td className="hof-num">{h.titles || ''}</td>
                <td className="hof-num">{h.runner_ups || ''}</td>
                <td className="hof-num">{h.podiums || ''}</td>
                <td className="hof-num">{h.seasons_played}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
